import {
    ArrowForward as ArrowForwardIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    PlayCircle as PlayCircleIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { ENotificationType } from '@sharedInterfaces/INotification';
import { selectCurrentUserOnboardingTalk } from '@store/reducer/employeeTalksReducer';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BaseWidget } from './BaseWidget';
import type { OnboardingStatus } from './NotificationTypes';

// ============================================================================
// INTERFACES
// ============================================================================

interface OnboardingCardWidgetProps {
    status?: OnboardingStatus;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * OnboardingCardWidget displays onboarding status and call-to-action.
 * Legacy component - consider using OnboardingStatusWidget for new implementations.
 *
 * @param {OnboardingCardWidgetProps} props - The component props
 * @returns {JSX.Element} The rendered OnboardingCardWidget component
 */
function OnboardingCardWidget({ status: _status }: OnboardingCardWidgetProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const onboardingTalk = useSelector(selectCurrentUserOnboardingTalk);

    // Return null if no onboarding talk exists
    if (!onboardingTalk) return null;

    const isCompleted = onboardingTalk.employeeConfirmed;
    const currentStep = onboardingTalk.talkState?.navigation?.currentStep || 0;
    const hasStarted = currentStep > 0;

    const currentStatus: OnboardingStatus = {
        isCompleted,
        progress: hasStarted ? Math.round(((currentStep + 1) / 6) * 100) : 0,
        type: isCompleted ? ENotificationType.SUCCESS : hasStarted ? ENotificationType.WARNING : ENotificationType.INFO,
        message: isCompleted
            ? 'Ihr Onboarding ist abgeschlossen. Sie können jederzeit Ihr Profil erweitern.'
            : hasStarted
              ? `Ihr Onboarding ist zu ${Math.round(((currentStep + 1) / 6) * 100)}% abgeschlossen.`
              : 'Beginnen Sie Ihren Onboarding-Prozess, um Ihr Profil zu vervollständigen.',
    };

    // ============================================================================
    // HELPER FUNCTIONS
    // ============================================================================

    const getStatusIcon = () => {
        switch (currentStatus.type) {
            case ENotificationType.SUCCESS:
                return <CheckCircleIcon color="success" />;
            case ENotificationType.WARNING:
                return <WarningIcon color="warning" />;
            case ENotificationType.ERROR:
                return <ErrorIcon color="error" />;
            case ENotificationType.INFO:
                return <InfoIcon color="info" />;
            default:
                return <PlayCircleIcon color="primary" />;
        }
    };

    const getStatusColor = () => {
        switch (currentStatus.type) {
            case ENotificationType.SUCCESS:
                return theme.palette.success.main;
            case ENotificationType.WARNING:
                return theme.palette.warning.main;
            case ENotificationType.ERROR:
                return theme.palette.error.main;
            case ENotificationType.INFO:
                return theme.palette.info.main;
            default:
                return theme.palette.primary.main;
        }
    };

    // ============================================================================
    // RENDER
    // ============================================================================

    return (
        <BaseWidget>
            <Box
                sx={{
                    p: 2,
                    borderLeft: `3px solid ${getStatusColor()}`,
                    borderRight: `3px solid ${getStatusColor()}`,
                    bgcolor: theme.palette.background.paper,
                }}
            >
                {/* Header with Icon and Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ mr: 1, color: getStatusColor() }}>{getStatusIcon()}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isCompleted
                            ? 'Onboarding abgeschlossen'
                            : hasStarted
                              ? 'Onboarding in Bearbeitung'
                              : 'Onboarding ausstehend'}
                    </Typography>
                </Box>

                {/* Status Message */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {currentStatus?.message ||
                        'Beginnen Sie Ihren Onboarding-Prozess, um Ihr Profil zu vervollständigen.'}
                </Typography>

                {/* Action Button */}
                <Button
                    variant="contained"
                    color={
                        currentStatus?.type === ENotificationType.SUCCESS
                            ? 'success'
                            : currentStatus?.type === ENotificationType.WARNING
                              ? 'warning'
                              : currentStatus?.type === ENotificationType.ERROR
                                ? 'error'
                                : currentStatus?.type === ENotificationType.INFO
                                  ? 'info'
                                  : 'primary'
                    }
                    endIcon={<ArrowForwardIcon />}
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/${ELinks.EMPLOYEE_TALKS}/${onboardingTalk.id}`)}
                >
                    {isCompleted ? 'Ergebnisse ansehen' : hasStarted ? 'Onboarding fortsetzen' : 'Onboarding starten'}
                </Button>
            </Box>
        </BaseWidget>
    );
}

export default OnboardingCardWidget;
